import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import {
    RefreshCw, Download, TrendingUp, Users, Camera, Swords,
    GraduationCap, BarChart3, Eye, ArrowUpDown, ArrowUp, ArrowDown,
    CreditCard, XCircle, History, Video, FileText, Share2, Euro,
    Puzzle, Calendar, Newspaper, MessageCircle, ShoppingBag, BookOpen,
    Heart, Layers, Map, Shield, UserPlus, Flag, Sparkles, Rocket,
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, BarChart, Bar, Cell,
} from 'recharts';
import {
    useAnalyticsDashboard, useAnalyticsTimeSeries, useCaptureLikesSummary, useCaptureLikesAvgSeries,
    useEngagementFunnel, useCohortRetention,
    type CohortMetricType,
    type AnalyticsPeriod,
    type AnalyticsTimeSeriesMetric,
} from '@/hooks/useAdminAnalytics';
import { MetricCard } from '@/components/shared/MetricCard';
import {
    useFeedTopViewers,
    useFeedTopCaptures,
    type FeedTopViewersOrderBy,
    type FeedTopCapturesOrderBy,
    type OrderDir,
} from '@/hooks/useAdminFeedAnalytics';
import {
    useFeedRankingAnalytics,
} from '@/hooks/useFeedRankingAnalytics';
import {
    useTokenUsageSummary,
    useAiVideoCostSummary,
    useTokenUsageTimeseries,
    useAiVideoTimeseries,
    type TokenFeatureKey,
} from '@/hooks/useTokenUsageAnalytics';
import { format, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '@/lib/utils';
import {
    useFeatureAnalytics,
    buildFeatureHealthList,
    type FeatureKey,
} from '@/hooks/useFeatureAnalytics';
import { ProductPulsePanel } from '@/components/analytics/ProductPulsePanel';
import {
    ACTIVITY_PLATFORM_OPTIONS,
    activityMetricHelp,
    activitySeriesMetric,
    isActivityWindow,
    type ActivityPlatform,
} from '@/components/analytics/activitySeriesMetric';
import { CohortWeeksTable } from '@/components/analytics/CohortWeeksTable';
import { CommerceOutcomeCharts } from '@/components/analytics/CommerceOutcomeCharts';
import { MonetizationTrendChart } from '@/components/analytics/MonetizationTrendChart';
import { FirstCaptureConversionChart } from '@/components/analytics/FirstCaptureConversionChart';
import { GestionAnalyticsPanel } from '@/components/analytics/GestionAnalyticsPanel';
import { EarlyLandingPanel } from '@/components/analytics/EarlyLandingPanel';
import { ProSourcePanel } from '@/components/analytics/ProSourcePanel';
import { ScrollChipRow } from '@/components/analytics/ScrollChipRow';
import { ANALYTICS_DAY_OPTIONS, ANALYTICS_TIME_RANGES } from '@/components/analytics/periodOptions';
import { useSidebar } from '@/hooks/useSidebar';
import { resolveMediaUrl } from '@/lib/media-url';

// ─── Constants ──────────────────────────────────────────────────────────────

const COLORS = {
    ocean: 'hsl(199, 89%, 48%)',
    teal: 'hsl(172, 66%, 50%)',
    amber: 'hsl(38, 92%, 50%)',
    emerald: 'hsl(142, 71%, 45%)',
    purple: 'hsl(262, 83%, 58%)',
    rose: 'hsl(346, 77%, 55%)',
    indigo: 'hsl(234, 89%, 74%)',
    cyan: 'hsl(187, 85%, 45%)',
};

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

type ChartMetric = 'captures' | 'first_capture_rate' | 'active_users' | 'weekly_active_users' | 'monthly_active_users' | 'quiz_sessions' | 'registered_users' | 'subscribers' | 'capture_likes' | 'capture_likes_avg' | 'epescar_patrols' | 'licenses';
type MaterializedChartMetric = Exclude<ChartMetric, 'licenses' | 'first_capture_rate' | 'capture_likes_avg'>;

function isMaterializedChartMetric(metric: ChartMetric): metric is MaterializedChartMetric {
    return metric !== 'licenses' && metric !== 'first_capture_rate' && metric !== 'capture_likes_avg';
}
type CohortType = CohortMetricType;
type CohortUnit = 'days' | 'weeks';

const CHART_METRICS: { value: ChartMetric; label: string; color: string }[] = [
    { value: 'captures', label: 'Capturas', color: COLORS.teal },
    { value: 'first_capture_rate', label: '% 1ª capturas', color: COLORS.emerald },
    { value: 'capture_likes', label: 'Likes', color: COLORS.rose },
    { value: 'capture_likes_avg', label: 'Promedio', color: COLORS.amber },
    { value: 'epescar_patrols', label: 'e-Pescar', color: COLORS.cyan },
    { value: 'active_users', label: 'DAU', color: COLORS.ocean },
    { value: 'weekly_active_users', label: 'WAU', color: COLORS.purple },
    { value: 'monthly_active_users', label: 'MAU', color: COLORS.amber },
    { value: 'registered_users', label: 'Registros', color: COLORS.indigo },
    { value: 'subscribers', label: 'Suscriptores', color: COLORS.emerald },
    { value: 'quiz_sessions', label: 'Quizzes', color: COLORS.purple },
    { value: 'licenses', label: 'Licencias', color: COLORS.emerald },
];

const COHORT_TYPES: { value: CohortType; label: string }[] = [
    { value: 'login', label: 'Login' },
    { value: 'capture', label: 'Captura' },
    { value: 'quiz', label: 'Quiz' },
    { value: 'battle', label: 'Batalla' },
];

const TIME_RANGES = ANALYTICS_TIME_RANGES;
const FEED_TIME_RANGES = ANALYTICS_TIME_RANGES;

function formatAvg(value: number): string {
    return value.toLocaleString('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

function formatSeriesDay(val: unknown, withYear = false): string {
    const [year, month, day] = String(val ?? '').slice(0, 10).split('-');
    if (!year || !month || !day) return '';
    return withYear ? `${Number(day)}/${Number(month)}/${year}` : `${Number(day)}/${Number(month)}`;
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    const min = Math.floor(ms / 60_000);
    const sec = Math.floor((ms % 60_000) / 1000);
    return sec > 0 ? `${min}m ${sec}s` : `${min} min`;
}

// ─── Component ──────────────────────────────────────────────────────────────


const FEATURE_ICON_MAP: Record<string, React.ReactNode> = {
    Camera: <Camera size={18} />,
    Eye: <Eye size={18} />,
    GraduationCap: <GraduationCap size={18} />,
    Swords: <Swords size={18} />,
    Users: <Users size={18} />,
    Calendar: <Calendar size={18} />,
    BookOpen: <BookOpen size={18} />,
    Newspaper: <Newspaper size={18} />,
    FileText: <FileText size={18} />,
    MessageCircle: <MessageCircle size={18} />,
    ShoppingBag: <ShoppingBag size={18} />,
    Video: <Video size={18} />,
    Heart: <Heart size={18} />,
    Layers: <Layers size={18} />,
    Map: <Map size={18} />,
    Shield: <Shield size={18} />,
    UserPlus: <UserPlus size={18} />,
    Flag: <Flag size={18} />,
};

type AnalyticsTab = 'product' | 'landing' | 'funnel' | 'cohorts' | 'trends' | 'feed' | 'features' | 'subscriptions' | 'gestion';

const ANALYTICS_TABS: AnalyticsTab[] = [
    'product', 'landing', 'funnel', 'cohorts', 'trends', 'feed', 'features', 'subscriptions', 'gestion',
];

function isAnalyticsTab(value: string | null): value is AnalyticsTab {
    // Compat: antigua pestaña onboarding → Aterrizaje / onboarding
    if (value === 'onboarding') return false;
    return !!value && (ANALYTICS_TABS as string[]).includes(value);
}

export function AnalyticsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { isMobile } = useSidebar();
    const tabFromUrl = searchParams.get('tab');
    const sectionFromUrl = searchParams.get('section');
    const [activeTab, setActiveTab] = useState<AnalyticsTab>(() => {
        if (tabFromUrl === 'onboarding') return 'landing';
        return isAnalyticsTab(tabFromUrl) ? tabFromUrl : 'landing';
    });
    const [chartMetric, setChartMetric] = useState<ChartMetric>('captures');
    const [activityPlatform, setActivityPlatform] = useState<ActivityPlatform>('all');
    const [timeRange, setTimeRange] = useState('30');
    const [cohortType, setCohortType] = useState<CohortType>('login');
    const [cohortUnit, setCohortUnit] = useState<CohortUnit>('weeks');
    const [featDays, setFeatDays] = useState(30);
    const [productDays, setProductDays] = useState(30);
    const [landingDays, setLandingDays] = useState(7);

    useEffect(() => {
        if (tabFromUrl === 'onboarding') {
            setActiveTab('landing');
            setSearchParams({ tab: 'landing', section: 'onboarding' }, { replace: true });
            return;
        }
        if (isAnalyticsTab(tabFromUrl)) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl, setSearchParams]);

    useEffect(() => {
        const el = document.querySelector(`[data-analytics-tab="${activeTab}"]`);
        if (el instanceof HTMLElement) {
            el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        }
    }, [activeTab]);

    function selectTab(tab: AnalyticsTab, section?: string) {
        setActiveTab(tab);
        const next: Record<string, string> = { tab };
        if (tab === 'landing' && section) {
            next.section = section;
        }
        // Conservar lente de perfil si venimos de deep-link del Dashboard
        const exp = searchParams.get('experience');
        const water = searchParams.get('water');
        const modality = searchParams.get('modality');
        if (tab === 'landing') {
            if (exp) next.experience = exp;
            if (water) next.water = water;
            if (modality) next.modality = modality;
        }
        setSearchParams(next, { replace: true });
    }

    function setLandingSection(section: string) {
        const next: Record<string, string> = { tab: 'landing', section };
        const exp = searchParams.get('experience');
        const water = searchParams.get('water');
        const modality = searchParams.get('modality');
        if (exp) next.experience = exp;
        if (water) next.water = water;
        if (modality) next.modality = modality;
        setSearchParams(next, { replace: true });
    }

    const days = timeRange === 'all' ? null : parseInt(timeRange);
    const activityWindow = isActivityWindow(chartMetric) ? chartMetric : null;
    const isLicensesView = chartMetric === 'licenses';
    const isFirstCaptureView = chartMetric === 'first_capture_rate';
    const isLikesAvgView = chartMetric === 'capture_likes_avg';
    const usesMaterializedSeries = isMaterializedChartMetric(chartMetric);
    const seriesMetric: AnalyticsTimeSeriesMetric = activityWindow
        ? activitySeriesMetric(activityWindow, activityPlatform)
        : isMaterializedChartMetric(chartMetric)
            ? chartMetric
            : 'captures';

    const [feedTimeRange, setFeedTimeRange] = useState('30');
    const feedDays = feedTimeRange === 'all' ? null : parseInt(feedTimeRange);
    const rankingDays = feedDays ?? 30;

    const { data: feedRanking, isLoading: feedRankingLoading } = useFeedRankingAnalytics(rankingDays);

    const [funnelTimeRange, setFunnelTimeRange] = useState('all');
    const funnelDays = funnelTimeRange === 'all' ? null : Number.parseInt(funnelTimeRange, 10);
    const funnelRangeLabel = TIME_RANGES.find((range) => range.value === funnelTimeRange)?.label ?? 'Histórico';

    const [subsTimeRange, setSubsTimeRange] = useState('30');
    const subsDays = subsTimeRange === 'all' ? null : parseInt(subsTimeRange);
    const [subsChartMetric, setSubsChartMetric] = useState<TokenFeatureKey>('ai_video_generation');

    const { data: tsData, isLoading: tsLoading, isError: tsError } = useAnalyticsTimeSeries(
        seriesMetric,
        days,
        null,
        null,
        usesMaterializedSeries,
    );
    const { data: likesSummary, isLoading: likesSummaryLoading } = useCaptureLikesSummary(days);
    const { data: likesAvgSeries, isLoading: likesAvgLoading, isError: likesAvgError } = useCaptureLikesAvgSeries(
        days,
        isLikesAvgView,
    );
    const {
        data: funnel,
        isLoading: funnelLoading,
        isFetching: funnelFetching,
        isError: funnelError,
    } = useEngagementFunnel(funnelDays);
    const { data: cohorts, isLoading: cohortsLoading } = useCohortRetention(cohortType);
    const [viewersOrderBy, setViewersOrderBy] = useState<FeedTopViewersOrderBy>('total_view_duration_ms');
    const [viewersOrderDir, setViewersOrderDir] = useState<OrderDir>('desc');
    const [capturesOrderBy, setCapturesOrderBy] = useState<FeedTopCapturesOrderBy>('impressions');
    const [capturesOrderDir, setCapturesOrderDir] = useState<OrderDir>('desc');

    const { data: dashboardData } = useAnalyticsDashboard('month');
    const dashboardMetrics = dashboardData?.metrics;

    const { data: featuresData, isLoading: featuresLoading } = useFeatureAnalytics(featDays);
    const featureHealthList = featuresData ? buildFeatureHealthList(featuresData) : [];

    const { data: feedViewers, isLoading: feedViewersLoading } = useFeedTopViewers(
        feedDays,
        50,
        viewersOrderBy,
        viewersOrderDir
    );
    const { data: feedCaptures, isLoading: feedCapturesLoading } = useFeedTopCaptures(
        feedDays,
        50,
        capturesOrderBy,
        capturesOrderDir
    );

    const { data: tokenSummary, isLoading: tokenSummaryLoading } = useTokenUsageSummary(subsDays);
    const { data: aiVideoCost, isLoading: aiVideoCostLoading } = useAiVideoCostSummary(subsDays);
    const { data: tokenTsData, isLoading: tokenTsLoading } = useTokenUsageTimeseries(
        subsChartMetric === 'ai_video_generation' ? null : subsChartMetric,
        subsDays
    );
    const { data: aiVideoTsData, isLoading: aiVideoTsLoading } = useAiVideoTimeseries(subsDays);
    const subsChartData = subsChartMetric === 'ai_video_generation'
        ? (aiVideoTsData || []).map(d => ({ date: d.date, value: d.value }))
        : (tokenTsData || []).map(d => ({ date: d.date, value: d.value }));

    const handleViewersSort = (col: FeedTopViewersOrderBy) => {
        if (col === viewersOrderBy) {
            setViewersOrderDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
        } else {
            setViewersOrderBy(col);
            setViewersOrderDir('desc');
        }
    };

    const handleCapturesSort = (col: FeedTopCapturesOrderBy) => {
        if (col === capturesOrderBy) {
            setCapturesOrderDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
        } else {
            setCapturesOrderBy(col);
            setCapturesOrderDir('desc');
        }
    };

    const activeMetric = CHART_METRICS.find(m => m.value === chartMetric)!;
    const activityPlatformLabel = ACTIVITY_PLATFORM_OPTIONS.find((option) => option.value === activityPlatform)?.label;
    const seriesName = activityWindow && activityPlatform !== 'all' && activityPlatformLabel
        ? `${activeMetric.label} · ${activityPlatformLabel}`
        : activeMetric.label;

    const chartData = isLikesAvgView
        ? (likesAvgSeries || []).map(d => ({
            date: d.date,
            value: d.avgLikes,
            captures: d.captures,
            likes: d.likes,
        }))
        : (tsData || []).map(d => ({
            date: d.date,
            value: d.value as number | null,
            captures: 0,
            likes: 0,
        }));
    const chartLoading = isLikesAvgView ? likesAvgLoading : tsLoading;
    const chartFailed = isLikesAvgView ? likesAvgError : tsError;
    const chartHasValues = isLikesAvgView
        ? chartData.some(d => d.value != null)
        : chartData.length > 0;

    const funnelData = funnel ? [
        { name: 'Registrados', value: funnel.total_users, fill: COLORS.ocean },
        { name: '1 captura', value: funnel.users_1_capture, fill: COLORS.teal },
        { name: '3 capturas', value: funnel.users_3_captures, fill: COLORS.amber },
        { name: '5 capturas', value: funnel.users_5_captures, fill: COLORS.rose },
        { name: '10 capturas', value: funnel.users_10_captures, fill: COLORS.purple },
    ] : [];

    const TABS: { value: AnalyticsTab; label: string; icon: React.ReactNode }[] = [
        { value: 'landing', label: 'Aterrizaje', icon: <Rocket size={14} /> },
        { value: 'product', label: 'Producto', icon: <Sparkles size={14} /> },
        { value: 'funnel', label: 'Funnel capturas', icon: <BarChart3 size={14} /> },
        { value: 'cohorts', label: 'Cohortes', icon: <Users size={14} /> },
        { value: 'trends', label: 'Tendencias', icon: <TrendingUp size={14} /> },
        { value: 'feed', label: 'Feed', icon: <Eye size={14} /> },
        { value: 'features', label: 'Funcionalidades', icon: <Puzzle size={14} /> },
        { value: 'subscriptions', label: 'Suscripciones', icon: <CreditCard size={14} /> },
        { value: 'gestion', label: 'Gestiones', icon: <FileText size={14} /> },
    ];

    const chartTickMinGap = isMobile ? 36 : 24;
    const chartHeightClass = isMobile ? 'h-[280px]' : 'h-[400px]';

    return (
        <div className="space-y-6 min-w-0 max-w-full">
            <PageHeader
                title="Analytics"
                description="Actividad de usuarios por perfil · primeras sesiones · retención · producto"
            />

            {/* Suscriptores solo en pestañas de negocio/monetización */}
            {dashboardMetrics && (activeTab === 'product' || activeTab === 'subscriptions') && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
                    <MetricCard
                        title="Suscriptores activos"
                        value={dashboardMetrics.subscribers_active}
                        icon={<CreditCard size={18} />}
                        iconColor="text-emerald-400"
                    />
                    <MetricCard
                        title="Suscriptores cancelados"
                        value={dashboardMetrics.subscribers_canceled}
                        icon={<XCircle size={18} />}
                        iconColor="text-rose-400"
                    />
                    <MetricCard
                        title="Suscriptores acumulados"
                        value={dashboardMetrics.subscribers_accumulated}
                        icon={<History size={18} />}
                        iconColor="text-amber-400"
                    />
                </div>
            )}

            {/* Tab Selector — scroll horizontal sin ensanchar el layout */}
            <ScrollChipRow innerClassName="gap-1 p-1 bg-muted/50 rounded-lg">
                {TABS.map(t => (
                    <button
                        key={t.value}
                        type="button"
                        data-analytics-tab={t.value}
                        onClick={() => selectTab(t.value)}
                        className={cn(
                            'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shrink-0',
                            activeTab === t.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {t.icon}
                        {t.label}
                    </button>
                ))}
            </ScrollChipRow>

            {activeTab === 'gestion' && <GestionAnalyticsPanel days={Number(timeRange) || 30} />}

            {/* ─── Product Tab (vista PM) ───────────────────────────────── */}
            {activeTab === 'product' && (
                <ProductPulsePanel
                    days={productDays}
                    onDaysChange={setProductDays}
                    onNavigateTab={(tab, section) => selectTab(tab as AnalyticsTab, section)}
                />
            )}

            {/* ─── Aterrizaje (incluye onboarding + patrones) ───────────── */}
            {activeTab === 'landing' && (
                <EarlyLandingPanel
                    days={landingDays}
                    onDaysChange={setLandingDays}
                    initialSection={
                        tabFromUrl === 'onboarding'
                            ? 'onboarding'
                            : sectionFromUrl
                    }
                    onSectionChange={setLandingSection}
                    initialExperience={searchParams.get('experience')}
                    initialWater={searchParams.get('water')}
                    initialModality={searchParams.get('modality')}
                    onLensChange={(lens) => {
                        const next: Record<string, string> = {
                            tab: 'landing',
                            ...(sectionFromUrl ? { section: sectionFromUrl } : {}),
                        };
                        if (lens.experience !== 'all') next.experience = lens.experience;
                        if (lens.water !== 'all') next.water = lens.water;
                        if (lens.modality !== 'all') next.modality = lens.modality;
                        setSearchParams(next, { replace: true });
                    }}
                />
            )}

            {/* ─── Trends Tab ───────────────────────────────────────────── */}
            {activeTab === 'trends' && (
                <div className="space-y-4 min-w-0">
                    <div className="space-y-3 min-w-0">
                        <div className="flex flex-wrap gap-1.5 w-full min-w-0">
                            {CHART_METRICS.map(m => (
                                <button
                                    key={m.value}
                                    type="button"
                                    title={
                                        isActivityWindow(m.value)
                                            ? activityMetricHelp(
                                                m.value,
                                                m.value === chartMetric ? activityPlatform : 'all',
                                            )
                                            : m.value === 'epescar_patrols'
                                                ? 'Salidas e-Pescar iniciadas por día'
                                                : m.value === 'first_capture_rate'
                                                    ? 'De los usuarios registrados en el periodo, porcentaje que ha publicado al menos una captura'
                                                    : m.value === 'capture_likes_avg'
                                                        ? 'Likes actuales de las capturas publicadas cada día, partido por esas capturas'
                                                        : undefined
                                    }
                                    onClick={() => setChartMetric(m.value)}
                                    className={cn(
                                        'shrink-0 px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                                        chartMetric === m.value
                                            ? 'text-white shadow-sm'
                                            : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                    )}
                                    style={chartMetric === m.value ? { backgroundColor: m.color } : undefined}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        {activityWindow && (
                            <div className="flex flex-wrap gap-1 w-full min-w-0">
                                {ACTIVITY_PLATFORM_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        title={activityMetricHelp(activityWindow, option.value)}
                                        onClick={() => setActivityPlatform(option.value)}
                                        className={cn(
                                            'shrink-0 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                                            activityPlatform === option.value
                                                ? 'bg-muted text-foreground'
                                                : 'text-muted-foreground hover:text-foreground'
                                        )}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="flex flex-wrap gap-1 w-full min-w-0">
                            {TIME_RANGES.map(r => (
                                <button
                                    key={r.value}
                                    type="button"
                                    title={r.value === 'all' ? 'Serie acotada a los últimos 5 años por rendimiento' : undefined}
                                    onClick={() => setTimeRange(r.value)}
                                    className={cn(
                                        'shrink-0 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                                        timeRange === r.value
                                            ? 'bg-muted text-foreground'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                        Series hasta ayer (día cerrado, hora de Madrid). El día en curso no se pinta.
                    </p>
                    {activityWindow && (
                        <p className="text-[11px] text-muted-foreground">
                            {activityMetricHelp(activityWindow, activityPlatform)}
                        </p>
                    )}
                    {isLikesAvgView && (
                        <p className="text-[11px] text-muted-foreground">
                            Cada punto es el promedio de likes actuales de las capturas publicadas ese día.
                            Un día sin capturas no se pinta. El número de arriba es el del periodo completo.
                        </p>
                    )}

                    {(chartMetric === 'captures' || chartMetric === 'capture_likes' || isLikesAvgView) && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
                            {chartMetric === 'captures' && (
                                <div className="rounded-xl border border-border bg-card p-4 min-w-0">
                                    <p className="text-xs text-muted-foreground mb-1">Capturas creadas (periodo)</p>
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {likesSummaryLoading ? '—' : (likesSummary?.captures_created ?? 0).toLocaleString('es-ES')}
                                    </p>
                                </div>
                            )}
                            {chartMetric === 'capture_likes' && (
                                <div className="rounded-xl border border-border bg-card p-4 min-w-0">
                                    <p className="text-xs text-muted-foreground mb-1">Likes nuevos (periodo)</p>
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {likesSummaryLoading ? '—' : (likesSummary?.likes_created_in_period ?? 0).toLocaleString('es-ES')}
                                    </p>
                                </div>
                            )}
                            {isLikesAvgView && (
                                <div className="rounded-xl border border-border bg-card p-4 min-w-0">
                                    <p className="text-xs text-muted-foreground mb-1">Promedio likes / captura nueva</p>
                                    <p className="text-2xl font-bold text-foreground tabular-nums">
                                        {likesSummaryLoading
                                            ? '—'
                                            : formatAvg(likesSummary?.avg_likes_per_new_capture ?? 0)}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Likes actuales sobre capturas publicadas en el rango
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {isFirstCaptureView ? (
                        <FirstCaptureConversionChart days={days} isMobile={isMobile} />
                    ) : isLicensesView ? (
                        <CommerceOutcomeCharts days={days} isMobile={isMobile} />
                    ) : (
                    <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0">
                        <div className={cn(chartHeightClass, 'w-full min-w-0')}>
                            {chartLoading ? (
                                <div className="h-full flex items-center justify-center text-muted-foreground">
                                    <RefreshCw size={20} className="animate-spin" />
                                </div>
                            ) : chartFailed ? (
                                <div className="h-full flex items-center justify-center text-muted-foreground text-sm text-center px-4">
                                    No se pudo cargar la serie
                                </div>
                            ) : chartHasValues ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={chartData}
                                        margin={{
                                            top: 8,
                                            right: isMobile ? 4 : 10,
                                            bottom: isMobile ? 8 : 4,
                                            left: isMobile ? 0 : -10,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                            tickLine={false}
                                            axisLine={false}
                                            minTickGap={chartTickMinGap}
                                            interval="preserveStartEnd"
                                            tickFormatter={(val) => formatSeriesDay(val)}
                                        />
                                        <YAxis
                                            width={isLikesAvgView ? (isMobile ? 36 : 48) : (isMobile ? 28 : 40)}
                                            allowDecimals={isLikesAvgView}
                                            domain={isLikesAvgView ? [0, 'auto'] : undefined}
                                            tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={isLikesAvgView ? (val: number) => formatAvg(val) : undefined}
                                        />
                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            labelStyle={{ color: 'hsl(210 40% 98%)' }}
                                            labelFormatter={(val) => formatSeriesDay(val, true)}
                                            formatter={isLikesAvgView
                                                ? (value, _name, item) => {
                                                    const row = item?.payload as {
                                                        captures?: number;
                                                        likes?: number;
                                                        value?: number | null;
                                                    } | undefined;
                                                    if (row?.value == null) return ['Sin capturas', 'Promedio'];
                                                    const avg = Number(value);
                                                    return [
                                                        `${formatAvg(Number.isFinite(avg) ? avg : row.value)} · ${(row?.captures ?? 0).toLocaleString('es-ES')} capturas · ${(row?.likes ?? 0).toLocaleString('es-ES')} likes`,
                                                        'Promedio',
                                                    ];
                                                }
                                                : undefined}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={activeMetric.color}
                                            strokeWidth={2}
                                            dot={false}
                                            connectNulls={false}
                                            name={seriesName}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                                    {isLikesAvgView
                                        ? 'No hay capturas en el rango seleccionado'
                                        : 'No hay datos para el rango seleccionado'}
                                </div>
                            )}
                        </div>
                    </div>
                    )}

                    <MonetizationTrendChart days={days} isMobile={isMobile} />
                </div>
            )}

            {/* ─── Funnel Tab ───────────────────────────────────────────── */}
            {activeTab === 'funnel' && (
                <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        Usuarios registrados en el intervalo y cuántos de ellos han llegado a 1, 3, 5 y 10 capturas.
                        Los intervalos cierran en ayer (hora de Madrid). Histórico es toda la vida del producto.
                        Para primeras sesiones, días de vida y patrones de retención, usa{' '}
                        <button
                            type="button"
                            className="text-primary hover:underline"
                            onClick={() => selectTab('landing', 'patterns')}
                        >
                            Aterrizaje → Patrones
                        </button>
                        .
                    </p>
                    <div className="flex flex-wrap gap-1.5 min-w-0">
                        {TIME_RANGES.map((range) => (
                            <button
                                key={range.value}
                                type="button"
                                title={range.value === 'all'
                                    ? 'Todos los usuarios registrados, sin límite de fecha'
                                    : 'Registrados en ese intervalo, hasta ayer (hora de Madrid)'}
                                onClick={() => setFunnelTimeRange(range.value)}
                                className={cn(
                                    'px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shrink-0',
                                    funnelTimeRange === range.value
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {range.label}
                            </button>
                        ))}
                    </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Funnel Chart */}
                    <div className="rounded-xl border border-border bg-card p-5">
                        <div className="mb-4 flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-foreground">
                                Funnel de capturas · {funnelRangeLabel}
                            </h3>
                            {funnelFetching && !funnelLoading && (
                                <RefreshCw size={14} className="animate-spin text-muted-foreground" />
                            )}
                        </div>
                        <div className="h-[350px]">
                            {funnelLoading ? (
                                <div className="h-full flex items-center justify-center text-muted-foreground">
                                    <RefreshCw size={20} className="animate-spin" />
                                </div>
                            ) : funnelError ? (
                                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                                    No se pudo cargar el funnel
                                </div>
                            ) : funnelData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={funnelData} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: 'hsl(215 20% 55%)' }} />
                                        <YAxis
                                            type="category" dataKey="name"
                                            tick={{ fontSize: 12, fill: 'hsl(215 20% 65%)' }}
                                            width={90} axisLine={false} tickLine={false}
                                        />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                                            {funnelData.map((entry, i) => (
                                                <Cell key={i} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                                    No hay datos
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Conversion Rates */}
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold text-foreground mb-4">
                            Tasas de Conversión · {funnelRangeLabel}
                        </h3>
                        {funnelError ? (
                            <div className="py-12 text-center text-muted-foreground text-sm">No se pudo cargar el funnel</div>
                        ) : funnel?.conversion_rates ? (
                            <div className="space-y-5">
                                <ConversionStep
                                    from="Registro"
                                    to="1ª Captura"
                                    fromCount={funnel.total_users}
                                    toCount={funnel.users_1_capture}
                                    rate={funnel.conversion_rates.signup_to_first}
                                    color={COLORS.teal}
                                />
                                <ConversionStep
                                    from="1ª Captura"
                                    to="3ª Captura"
                                    fromCount={funnel.users_1_capture}
                                    toCount={funnel.users_3_captures}
                                    rate={funnel.conversion_rates.first_to_third}
                                    color={COLORS.amber}
                                />
                                <ConversionStep
                                    from="3ª Captura"
                                    to="5ª Captura"
                                    fromCount={funnel.users_3_captures}
                                    toCount={funnel.users_5_captures}
                                    rate={funnel.conversion_rates.third_to_fifth}
                                    color={COLORS.rose}
                                />
                                <ConversionStep
                                    from="5ª Captura"
                                    to="10ª Captura"
                                    fromCount={funnel.users_5_captures}
                                    toCount={funnel.users_10_captures}
                                    rate={funnel.conversion_rates.fifth_to_tenth}
                                    color={COLORS.purple}
                                />
                            </div>
                        ) : (
                            <div className="py-12 text-center text-muted-foreground text-sm">Cargando...</div>
                        )}
                    </div>
                </div>
                </div>
            )}

            {/* ─── Feed Tab ──────────────────────────────────────────────── */}
            {activeTab === 'feed' && (
                <div className="space-y-6">
                    {/* Ranker health */}
                    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <BarChart3 size={16} className="text-primary" />
                            Salud del ranker — últimos {rankingDays} días
                        </h3>
                        {feedRankingLoading ? (
                            <div className="py-8 flex justify-center">
                                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
                            </div>
                        ) : feedRanking?.summary ? (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <MetricCard
                                        title="Decisiones registradas"
                                        value={feedRanking.summary.rows_logged}
                                        icon={<Eye size={16} />}
                                        iconColor="text-primary"
                                    />
                                    <MetricCard
                                        title="Usuarios"
                                        value={feedRanking.summary.users_with_ranking}
                                        icon={<Users size={16} />}
                                        iconColor="text-teal-400"
                                    />
                                    <MetricCard
                                        title="Sesiones"
                                        value={feedRanking.summary.sessions}
                                        icon={<History size={16} />}
                                        iconColor="text-purple-400"
                                    />
                                    <MetricCard
                                        title="Posición media"
                                        value={Number(feedRanking.summary.avg_rank_position ?? 0)}
                                        icon={<TrendingUp size={16} />}
                                        iconColor="text-amber-400"
                                    />
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                    <div className="rounded-lg bg-muted/30 p-3">
                                        <p className="text-xs text-muted-foreground">Servidos con interacción previa</p>
                                        <p className="font-bold">{feedRanking.summary.interacted_served?.toLocaleString()}</p>
                                    </div>
                                    <div className="rounded-lg bg-muted/30 p-3">
                                        <p className="text-xs text-muted-foreground">Servidos sin impresión previa</p>
                                        <p className="font-bold">{feedRanking.summary.unseen_served?.toLocaleString()}</p>
                                    </div>
                                    <div className="rounded-lg bg-muted/30 p-3">
                                        <p className="text-xs text-muted-foreground">Penalización media</p>
                                        <p className="font-bold">{feedRanking.summary.avg_composite_penalty}</p>
                                    </div>
                                </div>
                                {feedRanking.by_pool?.length > 0 && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-border bg-muted/30">
                                                    <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Pool</th>
                                                    <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Filas</th>
                                                    <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Usuarios</th>
                                                    <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Penalización media</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border">
                                                {feedRanking.by_pool.map((row) => (
                                                    <tr key={row.pool}>
                                                        <td className="px-3 py-2 font-mono text-xs">{row.pool}</td>
                                                        <td className="px-3 py-2 text-right font-mono text-xs">{row.rows?.toLocaleString()}</td>
                                                        <td className="px-3 py-2 text-right font-mono text-xs">{row.users?.toLocaleString()}</td>
                                                        <td className="px-3 py-2 text-right font-mono text-xs">{row.avg_penalty}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-muted-foreground py-4 text-center">
                                Sin datos en feed_ranking_log para este periodo
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 min-w-0">
                        <div className="flex flex-wrap gap-1.5 min-w-0">
                            {FEED_TIME_RANGES.map(r => (
                                <button
                                    key={r.value}
                                    type="button"
                                    title={r.value === 'all' ? 'Ranking acotado a los últimos 5 años por rendimiento' : undefined}
                                    onClick={() => setFeedTimeRange(r.value)}
                                    className={cn(
                                        'px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shrink-0',
                                        feedTimeRange === r.value
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Top usuarios por visualización */}
                        <div className="rounded-xl border border-border bg-card overflow-hidden">
                            <h3 className="text-sm font-semibold text-foreground px-5 py-4 border-b border-border">
                                Usuarios con mayor visualización del feed
                            </h3>
                            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-muted/50 z-10">
                                        <tr className="border-b border-border">
                                            <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">#</th>
                                            <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">Usuario</th>
                                            <SortableTh
                                                label="Impresiones"
                                                col="total_impressions"
                                                activeCol={viewersOrderBy}
                                                dir={viewersOrderDir}
                                                onSort={() => handleViewersSort('total_impressions')}
                                                align="right"
                                            />
                                            <SortableTh
                                                label="Tiempo total"
                                                col="total_view_duration_ms"
                                                activeCol={viewersOrderBy}
                                                dir={viewersOrderDir}
                                                onSort={() => handleViewersSort('total_view_duration_ms')}
                                                align="right"
                                            />
                                            <SortableTh
                                                label="Tiempo medio"
                                                col="avg_duration_ms"
                                                activeCol={viewersOrderBy}
                                                dir={viewersOrderDir}
                                                onSort={() => handleViewersSort('avg_duration_ms')}
                                                align="right"
                                            />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {feedViewersLoading ? (
                                            <tr>
                                                <td colSpan={5} className="py-12 text-center text-muted-foreground">
                                                    <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                                                    Cargando...
                                                </td>
                                            </tr>
                                        ) : (feedViewers ?? []).length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="py-12 text-center text-muted-foreground text-sm">
                                                    No hay datos de impresiones del feed
                                                </td>
                                            </tr>
                                        ) : (
                                            (feedViewers ?? []).slice(0, 50).map((row, idx) => (
                                                <tr key={row.user_id} className="hover:bg-muted/20 transition-colors">
                                                    <td className="px-4 py-2 font-medium text-muted-foreground">{idx + 1}</td>
                                                    <td className="px-4 py-2">
                                                        <div className="flex items-center gap-2">
                                                            {(() => {
                                                                const avatarSrc = resolveMediaUrl(row.avatar_url, 'profile-images');
                                                                return avatarSrc ? (
                                                                    <img src={avatarSrc} alt="" className="w-8 h-8 rounded-full object-cover" />
                                                                ) : (
                                                                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                                                                        {(row.display_name || '?')[0]}
                                                                    </div>
                                                                );
                                                            })()}
                                                            <span className="font-medium truncate max-w-[120px]">{row.display_name || 'Sin nombre'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2 text-right font-mono text-xs">{Number(row.total_impressions).toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-right font-mono text-xs">{formatDuration(row.total_view_duration_ms)}</td>
                                                    <td className="px-4 py-2 text-right font-mono text-xs">{row.avg_duration_ms != null ? formatDuration(row.avg_duration_ms) : '—'}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Top capturas más vistas */}
                        <div className="rounded-xl border border-border bg-card overflow-hidden">
                            <h3 className="text-sm font-semibold text-foreground px-5 py-4 border-b border-border">
                                Top capturas más vistas
                            </h3>
                            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-muted/50 z-10">
                                        <tr className="border-b border-border">
                                            <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">#</th>
                                            <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">Imagen</th>
                                            <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground">Especie / Autor</th>
                                            <SortableTh
                                                label="Impresiones"
                                                col="impressions"
                                                activeCol={capturesOrderBy}
                                                dir={capturesOrderDir}
                                                onSort={() => handleCapturesSort('impressions')}
                                                align="right"
                                            />
                                            <SortableTh
                                                label="Tiempo total"
                                                col="time"
                                                activeCol={capturesOrderBy}
                                                dir={capturesOrderDir}
                                                onSort={() => handleCapturesSort('time')}
                                                align="right"
                                            />
                                            <SortableTh
                                                label="Likes"
                                                col="likes"
                                                activeCol={capturesOrderBy}
                                                dir={capturesOrderDir}
                                                onSort={() => handleCapturesSort('likes')}
                                                align="right"
                                            />
                                            <SortableTh
                                                label="Comentarios"
                                                col="comments"
                                                activeCol={capturesOrderBy}
                                                dir={capturesOrderDir}
                                                onSort={() => handleCapturesSort('comments')}
                                                align="right"
                                            />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {feedCapturesLoading ? (
                                            <tr>
                                                <td colSpan={7} className="py-12 text-center text-muted-foreground">
                                                    <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                                                    Cargando...
                                                </td>
                                            </tr>
                                        ) : (feedCaptures ?? []).length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-12 text-center text-muted-foreground text-sm">
                                                    No hay datos de capturas vistas
                                                </td>
                                            </tr>
                                        ) : (
                                            (feedCaptures ?? []).slice(0, 50).map((row, idx) => {
                                                const imgUrl = resolveMediaUrl(row.image, 'capture-media');
                                                return (
                                                    <tr key={row.capture_id} className="hover:bg-muted/20 transition-colors">
                                                        <td className="px-4 py-2 font-medium text-muted-foreground">{idx + 1}</td>
                                                        <td className="px-4 py-2">
                                                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                                                {imgUrl ? (
                                                                    <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                                                        <Camera size={16} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <div>
                                                                <p className="font-medium truncate max-w-[140px]">{row.species || '—'}</p>
                                                                <p className="text-xs text-muted-foreground truncate max-w-[140px]">{row.author_name || '—'}</p>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2 text-right font-mono text-xs">{Number(row.impressions_count).toLocaleString()}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-xs">{formatDuration(row.total_view_duration_ms)}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-xs">{Number(row.likes_count ?? 0).toLocaleString()}</td>
                                                        <td className="px-4 py-2 text-right font-mono text-xs">{Number(row.comments_count ?? 0).toLocaleString()}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Subscriptions (Pro + Token Usage) Tab ─────────────────── */}
            {activeTab === 'subscriptions' && (
                <div className="space-y-6">
                    <ProSourcePanel days={subsDays ?? 30} />

                    <div className="flex flex-wrap items-center gap-3 min-w-0">
                        <div className="flex flex-wrap gap-1.5 min-w-0">
                            {TIME_RANGES.map(r => (
                                <button
                                    key={r.value}
                                    type="button"
                                    title={r.value === 'all' ? 'Resumen de tokens: últimos 30 días (API por defecto)' : undefined}
                                    onClick={() => setSubsTimeRange(r.value)}
                                    className={cn(
                                        'px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shrink-0',
                                        subsTimeRange === r.value
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* KPIs: Uso de tokens y costes */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {aiVideoCostLoading ? (
                            <div className="col-span-4 py-8 flex justify-center">
                                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <>
                                <MetricCard
                                    title="Videos IA generados"
                                    value={aiVideoCost?.total_videos ?? 0}
                                    icon={<Video size={18} />}
                                    iconColor="text-violet-400"
                                />
                                <MetricCard
                                    title="Coste videos IA (0,04 €/video)"
                                    value={formatCurrency(aiVideoCost?.cost_eur ?? 0)}
                                    icon={<Euro size={18} />}
                                    iconColor="text-amber-400"
                                    format="none"
                                />
                                <MetricCard
                                    title="Reportes Pescadillo"
                                    value={(tokenSummary?.find(s => s.feature_key === 'ai_agent')?.total_uses ?? 0)}
                                    icon={<FileText size={18} />}
                                    iconColor="text-teal-400"
                                />
                                <MetricCard
                                    title="Promociones al feed"
                                    value={(tokenSummary?.find(s => s.feature_key === 'promote_to_feed')?.total_uses ?? 0)}
                                    icon={<Share2 size={18} />}
                                    iconColor="text-rose-400"
                                />
                            </>
                        )}
                    </div>

                    {/* Resumen por feature */}
                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <h3 className="text-sm font-semibold text-foreground px-5 py-4 border-b border-border">
                            Consumo de tokens por feature
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-muted/30">
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Feature</th>
                                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Usos</th>
                                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Tokens consumidos</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {tokenSummaryLoading ? (
                                        <tr>
                                            <td colSpan={3} className="py-8 text-center">
                                                <RefreshCw size={20} className="animate-spin mx-auto text-muted-foreground" />
                                            </td>
                                        </tr>
                                    ) : (tokenSummary ?? []).length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="py-8 text-center text-muted-foreground text-sm">
                                                No hay datos de consumo en el período seleccionado
                                            </td>
                                        </tr>
                                    ) : (
                                        (tokenSummary ?? []).map(row => (
                                            <tr key={row.feature_key} className="hover:bg-muted/20">
                                                <td className="px-4 py-2 font-medium">{row.feature_label}</td>
                                                <td className="px-4 py-2 text-right font-mono">{row.total_uses.toLocaleString()}</td>
                                                <td className="px-4 py-2 text-right font-mono">{row.total_tokens_consumed.toLocaleString()}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Gráfico de tendencias por feature */}
                    <div className="rounded-xl border border-border bg-card p-5">
                        <h3 className="text-sm font-semibold text-foreground mb-4">Tendencias de uso (por día)</h3>
                        <div className="flex gap-1.5 mb-4">
                            {[
                                { value: 'ai_video_generation' as TokenFeatureKey, label: 'Videos IA', color: COLORS.purple },
                                { value: 'ai_agent' as TokenFeatureKey, label: 'Reportes', color: COLORS.teal },
                                { value: 'promote_to_feed' as TokenFeatureKey, label: 'Promociones', color: COLORS.rose },
                            ].map(m => (
                                <button
                                    key={m.value}
                                    onClick={() => setSubsChartMetric(m.value)}
                                    className={cn(
                                        'px-3 py-1 rounded-full text-xs font-medium transition-all',
                                        subsChartMetric === m.value ? 'text-white shadow-sm' : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                    )}
                                    style={subsChartMetric === m.value ? { backgroundColor: m.color } : undefined}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        <div className="h-[350px]">
                            {(subsChartMetric === 'ai_video_generation' ? aiVideoTsLoading : tokenTsLoading) ? (
                                <div className="h-full flex items-center justify-center text-muted-foreground">
                                    <RefreshCw size={20} className="animate-spin" />
                                </div>
                            ) : subsChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={subsChartData} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 11, fill: 'hsl(215 20% 55%)' }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={val => {
                                                if (!val) return '';
                                                const d = new Date(val);
                                                return `${d.getDate()}/${d.getMonth() + 1}`;
                                            }}
                                        />
                                        <YAxis tick={{ fontSize: 11, fill: 'hsl(215 20% 55%)' }} tickLine={false} axisLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'hsl(210 40% 98%)' }} />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={subsChartMetric === 'ai_video_generation' ? COLORS.purple : subsChartMetric === 'ai_agent' ? COLORS.teal : COLORS.rose}
                                            strokeWidth={2}
                                            dot={false}
                                            name={subsChartMetric === 'ai_video_generation' ? 'Videos IA' : subsChartMetric === 'ai_agent' ? 'Reportes' : 'Promociones'}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                                    No hay datos para el rango seleccionado
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Cohorts Tab ──────────────────────────────────────────── */}
            {activeTab === 'cohorts' && (
                <div className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        La pesca no es diaria. En Semanas, volver el fin de semana siguiente cuenta como W1.
                        Días sigue siendo el calendario D1–D30 tras el alta.
                    </p>
                    <div className="flex flex-wrap gap-1.5 min-w-0">
                        {(['weeks', 'days'] as const).map((unit) => (
                            <button
                                key={unit}
                                type="button"
                                onClick={() => setCohortUnit(unit)}
                                className={cn(
                                    'px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all',
                                    cohortUnit === unit
                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                        : 'bg-muted/50 text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {unit === 'weeks' ? 'Semanas' : 'Días'}
                            </button>
                        ))}
                    </div>
                    {/* Cohort type selector */}
                    <div className="flex flex-wrap gap-1.5 min-w-0">
                        {COHORT_TYPES.map(t => (
                            <button
                                key={t.value}
                                type="button"
                                onClick={() => setCohortType(t.value)}
                                className={cn(
                                    'px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shrink-0',
                                    cohortType === t.value
                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                        : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {cohortUnit === 'weeks' ? (
                        <CohortWeeksTable metricType={cohortType} />
                    ) : (
                    /* Cohort Table */
                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-muted/30">
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Semana</th>
                                        <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Inicial</th>
                                        {['1', '3', '5', '7', '10', '20', '30'].map(d => (
                                            <th key={d} className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                D{d}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {cohortsLoading ? (
                                        <tr>
                                            <td colSpan={9} className="py-12 text-center text-muted-foreground">
                                                <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                                                Cargando cohortes...
                                            </td>
                                        </tr>
                                    ) : !cohorts || cohorts.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="py-12 text-center text-muted-foreground">
                                                No hay datos de cohortes para "{COHORT_TYPES.find(t => t.value === cohortType)?.label}"
                                            </td>
                                        </tr>
                                    ) : (
                                        cohorts.slice(0, 15).map((row, idx) => {
                                            const weekStart = new Date(row.cohort_date);
                                            const daysSince = differenceInDays(new Date(), weekStart);

                                            return (
                                                <tr key={idx} className="hover:bg-muted/20 transition-colors">
                                                    <td className="px-4 py-2">
                                                        <div className="text-xs">
                                                            <p className="font-medium text-foreground">
                                                                {format(weekStart, 'dd/MM', { locale: es })}
                                                            </p>
                                                            <p className="text-muted-foreground">
                                                                {format(weekStart, 'MMM yy', { locale: es })}
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 text-center font-bold text-foreground">
                                                        {row.initial_count}
                                                    </td>
                                                    <CohortCell pct={row.day_1_pct} isFuture={daysSince < 1} />
                                                    <CohortCell pct={row.day_3_pct} isFuture={daysSince < 3} />
                                                    <CohortCell pct={row.day_5_pct} isFuture={daysSince < 5} />
                                                    <CohortCell pct={row.day_7_pct} isFuture={daysSince < 7} />
                                                    <CohortCell pct={row.day_10_pct} isFuture={daysSince < 10} />
                                                    <CohortCell pct={row.day_20_pct} isFuture={daysSince < 20} />
                                                    <CohortCell pct={row.day_30_pct} isFuture={daysSince < 30} />
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    )}
                </div>
            )}

            {/* ─── Features Tab ─────────────────────────────────────────── */}
            {activeTab === 'features' && (
                <div className="space-y-6">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex gap-1.5 flex-wrap">
                            {ANALYTICS_DAY_OPTIONS.map(d => (
                                <button
                                    key={d}
                                    onClick={() => setFeatDays(d)}
                                    className={cn(
                                        'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                                        featDays === d
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    {d} días
                                </button>
                            ))}
                        </div>
                    </div>

                    {featuresLoading ? (
                        <div className="py-16 flex justify-center">
                            <RefreshCw size={24} className="animate-spin text-muted-foreground" />
                        </div>
                    ) : featureHealthList.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground text-sm">
                            No hay datos disponibles
                        </div>
                    ) : (
                        <>
                            {/* Feature Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {featureHealthList
                                    .sort((a, b) => b.activity - a.activity)
                                    .map(feat => {
                                        const iconNode = FEATURE_ICON_MAP[feat.icon] || <Puzzle size={18} />;
                                        const activityColor = feat.activity > 100 ? 'text-emerald-400'
                                            : feat.activity > 10 ? 'text-amber-400'
                                                : feat.activity > 0 ? 'text-orange-400'
                                                    : 'text-red-400';

                                        return (
                                            <div
                                                key={feat.key}
                                                className="rounded-xl border border-border bg-card p-4 hover:border-primary/20 transition-colors"
                                            >
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className={cn('p-2 rounded-lg bg-muted/50', activityColor)}>
                                                        {iconNode}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-sm font-semibold text-foreground truncate">
                                                            {feat.label}
                                                        </h4>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                                                            {feat.activityCaption ?? 'Actividad'}
                                                        </p>
                                                        <p className="text-lg font-bold text-foreground">
                                                            {feat.activity.toLocaleString()}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                                                            Usuarios
                                                        </p>
                                                        <p className="text-lg font-bold text-foreground">
                                                            {feat.users == null ? '—' : feat.users.toLocaleString()}
                                                        </p>
                                                    </div>
                                                </div>
                                                {feat.note && (
                                                    <p className="mt-2 text-[10px] text-muted-foreground leading-snug">{feat.note}</p>
                                                )}

                                                {/* Activity bar */}
                                                <div className="mt-3">
                                                    <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                                                        <div
                                                            className={cn('h-full rounded-full transition-all', activityColor.replace('text-', 'bg-'))}
                                                            style={{
                                                                width: `${Math.min(
                                                                    100,
                                                                    featureHealthList.length > 0
                                                                        ? (feat.activity / Math.max(...featureHealthList.map(f => f.activity), 1)) * 100
                                                                        : 0
                                                                )}%`,
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>

                            {/* Feature Table */}
                            <div className="rounded-xl border border-border bg-card overflow-hidden">
                                <h3 className="text-sm font-semibold text-foreground px-5 py-4 border-b border-border">
                                    Detalle por funcionalidad — Últimos {featDays} días
                                </h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-border bg-muted/30">
                                                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Funcionalidad</th>
                                                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Actividad</th>
                                                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Usuarios</th>
                                                <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Ratio act/usr</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                            {featureHealthList
                                                .sort((a, b) => b.activity - a.activity)
                                                .map(feat => {
                                                    const ratio = feat.users != null && feat.users > 0
                                                        ? (feat.activity / feat.users).toFixed(1)
                                                        : '—';
                                                    return (
                                                        <tr key={feat.key} className="hover:bg-muted/20 transition-colors">
                                                            <td className="px-4 py-2">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-muted-foreground">
                                                                        {FEATURE_ICON_MAP[feat.icon] || <Puzzle size={16} />}
                                                                    </span>
                                                                    <span className="font-medium">{feat.label}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-2 text-right font-mono text-xs">
                                                                {feat.activity.toLocaleString()}
                                                            </td>
                                                            <td className="px-4 py-2 text-right font-mono text-xs">
                                                                {feat.users == null ? '—' : feat.users.toLocaleString()}
                                                                {feat.note && (
                                                                    <p className="text-[10px] text-muted-foreground font-sans mt-0.5">{feat.note}</p>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 text-right font-mono text-xs text-muted-foreground">
                                                                {ratio}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

        </div>
    );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SortableTh({
    label,
    col,
    activeCol,
    dir,
    onSort,
    align = 'left',
}: {
    label: string;
    col: string;
    activeCol: string;
    dir: 'asc' | 'desc';
    onSort: () => void;
    align?: 'left' | 'right';
}) {
    const isActive = activeCol === col;
    return (
        <th
            className={cn(
                'px-4 py-2 text-xs font-semibold text-muted-foreground cursor-pointer hover:text-foreground select-none transition-colors',
                align === 'right' && 'text-right'
            )}
            onClick={onSort}
        >
            <span className="inline-flex items-center gap-1">
                {label}
                {isActive ? (
                    dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                ) : (
                    <ArrowUpDown size={12} className="opacity-50" />
                )}
            </span>
        </th>
    );
}

function CohortCell({ pct, isFuture }: { pct: number | null; isFuture: boolean }) {
    if (isFuture) {
        return (
            <td className="px-3 py-2 text-center">
                <span className="text-xs text-muted-foreground/30">—</span>
            </td>
        );
    }

    const value = pct == null ? 0 : Number(pct);
    const safeValue = Number.isFinite(value) ? value : 0;
    const bg = safeValue >= 80 ? 'bg-emerald-500'
        : safeValue >= 60 ? 'bg-emerald-400'
            : safeValue >= 40 ? 'bg-amber-400'
                : safeValue >= 20 ? 'bg-orange-400'
                    : safeValue > 0 ? 'bg-red-400'
                        : 'bg-muted/50';

    return (
        <td className="px-3 py-2 text-center">
            <div className="inline-flex items-center gap-1">
                <div className={cn('w-8 h-4 rounded', bg)} style={{ opacity: 0.7 + (safeValue / 300) }} />
                <span className="text-xs font-medium text-foreground min-w-[32px]">{safeValue.toFixed(1)}%</span>
            </div>
        </td>
    );
}

function ConversionStep({ from, to, fromCount, toCount, rate, color }: {
    from: string;
    to: string;
    fromCount: number;
    toCount: number;
    rate: number;
    color: string;
}) {
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{from}</span> → <span className="font-medium text-foreground">{to}</span>
                </span>
                <span className="font-bold text-foreground">{rate.toFixed(1)}%</span>
            </div>
            <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-16 text-right">{fromCount.toLocaleString()}</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.min(rate, 100)}%`, backgroundColor: color }}
                    />
                </div>
                <span className="text-xs font-medium text-foreground w-16">{toCount.toLocaleString()}</span>
            </div>
        </div>
    );
}
